import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/services", label: "Services", testid: "nav-services" },
  { to: "/gallery", label: "Gallery", testid: "nav-gallery" },
  { to: "/contact", label: "Contact Us", testid: "nav-contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-[#fbf9f5]/85 backdrop-blur-xl border-b border-black/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
          <img
            src="/assets/logo.png"
            alt="Eunice 25 logo — red paint roller"
            className="h-14 w-auto object-contain group-hover:scale-105 transition-transform"
          />
          <div className="leading-tight">
            <span className="font-display font-black text-xl tracking-tight text-[#121212]">
              EUNICE <span className="text-[#c8102e]">25</span>
            </span>
            <span className="block font-mono2 text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              Painting & Decorating
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `link-sweep text-sm font-semibold tracking-wide transition-colors ${
                  isActive ? "text-[#c8102e]" : "text-[#121212] hover:text-[#c8102e]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={COMPANY.phone1Href}
            data-testid="header-call-btn"
            className="flex items-center gap-2 bg-[#c8102e] hover:bg-[#a00d24] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone1}
          </a>
        </nav>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-[#121212]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#fbf9f5] border-t border-black/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`${l.testid}-mobile`}
                  className={({ isActive }) =>
                    `font-display text-2xl font-bold ${
                      isActive || location.pathname === l.to ? "text-[#c8102e]" : "text-[#121212]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={COMPANY.phone1Href}
                data-testid="mobile-call-btn"
                className="flex items-center justify-center gap-2 bg-[#c8102e] text-white font-bold px-5 py-3 rounded-full"
              >
                <Phone className="w-4 h-4" /> Call {COMPANY.phone1}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
